import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Crown, TrendingUp, Target, Settings, PieChart, Scale, Search, Headphones, Package, Lock } from 'lucide-react';

const AGENTS = [
  { icon: Crown, name: 'Executive Agent', short: 'Leadership clarity and strategic follow-up.',
    detail: 'Prepares briefings, structures priorities, summarizes key meetings, tracks action items, builds executive notes, and supports strategic follow-up across leadership workflows.' },
  { icon: TrendingUp, name: 'Sales Agent', short: 'Pipeline intelligence and outreach support.',
    detail: 'Enriches leads, drafts outreach logic, supports proposals, tracks follow-up, surfaces high-priority opportunities, and strengthens pipeline intelligence.' },
  { icon: Target, name: 'Marketing Agent', short: 'Campaign support and content structuring.',
    detail: 'Creates campaign support, structures content pipelines, drafts copy, analyzes market messaging, monitors competitors, and assists GTM execution.' },
  { icon: Settings, name: 'Operations Agent', short: 'Workflow coordination and execution support.',
    detail: 'Coordinates workflows, organizes internal processes, reduces administrative friction, tracks tasks, surfaces bottlenecks, and supports execution across teams.' },
  { icon: PieChart, name: 'Finance Agent', short: 'Reporting preparation and financial structuring.',
    detail: 'Supports reporting preparation, classifies financial inputs, tracks anomalies, organizes billing and payment data, and structures internal financial overviews.' },
  { icon: Scale, name: 'Legal / Compliance Agent', short: 'Contract screening and risk support.',
    detail: 'Screens contracts, highlights clauses, prepares risk summaries, supports due diligence workflows, structures document review, and maintains compliance discipline.' },
  { icon: Search, name: 'Research / Intelligence Agent', short: 'Industry monitoring and decision support.',
    detail: 'Monitors industries, structures research, summarizes reports, tracks competitors, builds internal intelligence packs, and supports decision-making workflows.' },
  { icon: Headphones, name: 'Customer Service Agent', short: 'Service consistency and escalation routing.',
    detail: 'Prepares responses, classifies support requests, routes escalations, maintains service consistency, and supports multilingual service workflows.' },
  { icon: Package, name: 'Procurement / Vendor Agent', short: 'Supplier tracking and purchasing support.',
    detail: 'Tracks supplier workflows, structures purchasing support, monitors vendor details, and prepares internal overviews for procurement decisions.' },
  { icon: Lock, name: 'Private Office Agent', short: 'Executive and family office coordination.',
    detail: 'Executive support, travel and administrative coordination, priority organization, contact and document management. Highly relevant for family offices and operators.' },
];

export default function CapabilityGrid() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="capabilities" className="relative py-24 sm:py-32" data-testid="capability-grid-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="overline mb-4">Agent Capabilities</div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
            A specialist agent for every domain.
          </h2>
          <p className="text-base text-aurocle-platinum-muted max-w-2xl mb-16">
            Aurocle deploys coordinated agents across departments — each structured for its domain, all working under unified orchestration and control.
          </p>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {AGENTS.map((agent, i) => {
            const Icon = agent.icon;
            const isHovered = hovered === i;
            return (
              <ScrollReveal key={i} delay={i * 60}>
                <div
                  className="premium-card p-6 cursor-default group h-full"
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  data-testid={`agent-card-${i}`}
                >
                  <div className="relative z-10">
                    <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-aurocle-gold/30 transition-colors mb-4">
                      <Icon size={18} className="text-aurocle-platinum-muted group-hover:text-aurocle-gold transition-colors" />
                    </div>
                    <h3 className="font-heading text-sm font-medium text-aurocle-text mb-2">{agent.name}</h3>
                    <p className="text-xs text-aurocle-platinum-muted leading-relaxed">
                      {isHovered ? agent.detail : agent.short}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
